import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

const POLL_FALLBACK_MS = 60_000

export default class extends Controller {
  static targets = ["panel", "badge", "list"]
  static values = { markUrl: String, previewUrl: String }

  connect() {
    this.boundOutsideClick = this.#outsideClick.bind(this)
    this.boundVisibility = this.#onVisibility.bind(this)
    this.pollTimer = null
    this.cableConnected = false
    this.consumer = null
    this.subscription = null

    this.refresh()
    this.#connectCable()
    this.#startFallbackPolling()
    document.addEventListener("visibilitychange", this.boundVisibility)
    this.boundOverlayOpen = this.#onOverlayOpen.bind(this)
    document.addEventListener("overlay:open", this.boundOverlayOpen)
  }

  disconnect() {
    this.#disconnectCable()
    this.#stopFallbackPolling()
    document.removeEventListener("visibilitychange", this.boundVisibility)
    document.removeEventListener("click", this.boundOutsideClick, true)
    document.removeEventListener("overlay:open", this.boundOverlayOpen)
  }

  #onOverlayOpen(event) {
    if (event.detail?.source !== "notifications") this.#closePanel()
  }

  toggle(event) {
    event.preventDefault()
    const opening = this.panelTarget.classList.contains("hidden")
    if (opening) {
      document.dispatchEvent(
        new CustomEvent("overlay:open", { detail: { source: "notifications" }, bubbles: true })
      )
      this.panelTarget.classList.remove("hidden")
      this.#markRead()
      queueMicrotask(() => {
        document.addEventListener("click", this.boundOutsideClick, true)
      })
    } else {
      this.#closePanel()
    }
  }

  pokeBackFinished(event) {
    if (event.detail.success === false) return

    this.#closePanel()
    this.#markRead()
  }

  #onVisibility() {
    if (document.visibilityState === "visible") {
      this.refresh()
    }
  }

  #connectCable() {
    const url =
      document.querySelector('meta[name="action-cable-url"]')?.content || "/cable"

    this.consumer = createConsumer(url)
    this.subscription = this.consumer.subscriptions.create(
      { channel: "NotificationsChannel" },
      {
        connected: () => {
          this.cableConnected = true
        },
        disconnected: () => {
          this.cableConnected = false
        },
        received: () => {
          this.refresh()
        }
      }
    )
  }

  #disconnectCable() {
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
    this.subscription = null
    this.consumer = null
    this.cableConnected = false
  }

  #startFallbackPolling() {
    if (!this.hasPreviewUrlValue) return
    this.pollTimer = window.setInterval(() => {
      if (!this.cableConnected) this.refresh()
    }, POLL_FALLBACK_MS)
  }

  #stopFallbackPolling() {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  async refresh() {
    if (!this.hasPreviewUrlValue) return

    try {
      const res = await fetch(this.previewUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return

      const data = await res.json()
      if (this.hasListTarget) {
        this.listTarget.innerHTML = data.list_html || ""
      }

      if (this.#panelOpen()) {
        await this.#markRead()
      } else {
        this.#updateBadge(data.unread_count)
      }
    } catch {
      // ignore network errors; fallback poll or next push will retry
    }
  }

  #panelOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")
  }

  #updateBadge(count) {
    const n = Number(count) || 0
    this.#syncUnreadBadges(n)
  }

  #syncUnreadBadges(n) {
    if (this.hasBadgeTarget) {
      const badge = this.badgeTarget
      if (n > 0) {
        badge.textContent = n > 99 ? "99+" : String(n)
        badge.classList.add("bg-red-500", "text-white", "shadow-sm")
        badge.classList.remove("invisible", "bg-transparent")
        badge.removeAttribute("aria-hidden")
      } else {
        badge.textContent = ""
        badge.classList.remove("bg-red-500", "text-white", "shadow-sm")
        badge.classList.add("invisible", "bg-transparent")
        badge.setAttribute("aria-hidden", "true")
      }
    }

    const drawerBadge = document.querySelector("[data-drawer-notification-badge]")
    if (!drawerBadge) return

    if (n > 0) {
      drawerBadge.textContent = n > 99 ? "99+" : String(n)
      drawerBadge.classList.remove("hidden")
      drawerBadge.removeAttribute("aria-hidden")
    } else {
      drawerBadge.textContent = ""
      drawerBadge.classList.add("hidden")
      drawerBadge.setAttribute("aria-hidden", "true")
    }
  }

  #outsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.#closePanel()
    }
  }

  #closePanel() {
    this.panelTarget.classList.add("hidden")
    document.removeEventListener("click", this.boundOutsideClick, true)
  }

  async #markRead() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (!token || !this.hasMarkUrlValue) return

    try {
      const res = await fetch(this.markUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
          Accept: "application/json",
        },
        credentials: "same-origin",
      })
      if (res.ok) this.#updateBadge(0)
    } catch {
      // ignore
    }
  }
}
